import { CSSObject, Global, css } from "@emotion/react";
import { CascadiaColor } from "@certn/cascadia-tokens";
import {
  CSSProperties,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from "react";
import { convertTokensToVariables } from "./GlobalStyles";
import { useScheme } from "../hooks/useColors";
import { darken, desaturate, lighten } from "color2k";
import { getTextColorForBackground } from "../utils/colorValidation";

interface BrandContextValue {
  colors: Partial<Record<CascadiaColor, string>>;
  isCobrandingEnabled: boolean;
}

const BrandContext = createContext<BrandContextValue>({
  colors: {},
  isCobrandingEnabled: false,
});

export function useBrandColors() {
  return useContext(BrandContext).colors;
}

/**
 * Hook to check if cobranding is enabled (i.e., a primary color is configured).
 * Use this hook in components to conditionally apply cobranding styles or features.
 *
 * @returns boolean - true if a primary color is configured
 *
 * @example
 * ```tsx
 * const cobrandingEnabled = useCobrandingEnabled();
 *
 * if (cobrandingEnabled) {
 *   return <BrandedComponent />;
 * }
 * ```
 */
export function useCobrandingEnabled(): boolean {
  return useContext(BrandContext).isCobrandingEnabled;
}

interface Props extends PropsWithChildren {
  primaryColor?: string;
  /**
   * When true, applies brand colors globally to the body element (affects modals/portals).
   * When false, scopes colors to a wrapper div (for preview purposes).
   * @default true
   */
  global?: boolean;
}

export function BrandProvider(props: Props) {
  const { primaryColor, children, global: isGlobal = true } = props;
  const scheme = useScheme();

  const colors = useMemo(
    () => generateBrandColors(primaryColor, scheme),
    [primaryColor, scheme]
  );

  const isCobrandingEnabled = Boolean(primaryColor);

  const contextValue: BrandContextValue = useMemo(
    () => ({
      colors,
      isCobrandingEnabled,
    }),
    [colors, isCobrandingEnabled]
  );

  if (isGlobal) {
    return (
      <BrandContext.Provider value={contextValue}>
        <Global
          styles={css({
            body: { ...(convertTokensToVariables(colors) as CSSObject) },
          })}
        />
        {children}
      </BrandContext.Provider>
    );
  }

  return (
    <BrandContext.Provider value={contextValue}>
      <div style={convertTokensToVariables(colors) as CSSProperties}>
        {children}
      </div>
    </BrandContext.Provider>
  );
}

function generateBrandColors(
  primaryColor: string | undefined,
  scheme: "light" | "dark" = "dark"
) {
  if (!primaryColor) {
    return {} as Record<CascadiaColor, string>;
  }

  const baseColors = {
    "text-on-brand": getTextColorForBackground(primaryColor),
    "background-brand-primary-strong": primaryColor,
  };

  if (scheme === "light") {
    return {
      ...baseColors,
      "background-brand-primary-strong-hover": darken(primaryColor, 0.04),
      // Lighter/muted version (not currently used)
      "background-brand-primary-weak": lighten(
        desaturate(primaryColor, 0.6),
        0.7
      ),
      // Hover state for weak (not currently used)
      "background-brand-primary-weak-hover": lighten(
        desaturate(primaryColor, 0.6),
        0.6
      ),
    };
  } else {
    return {
      ...baseColors,
      "background-brand-primary-strong-hover": lighten(primaryColor, 0.1),
      // Lighter/muted version (not currently used)
      "background-brand-primary-weak": darken(primaryColor, 0.425),
      // Hover state for weak (not currently used)
      "background-brand-primary-weak-hover": darken(primaryColor, 0.345),
    };
  }
}
